/**
 * Disclosures workspace — Clara / large position disclosures (bank demo).
 *
 * Left pane: list of disclosure cases.
 * Right pane: tabbed case detail — Summary, Documents, Assessment,
 * Peer Review (shadow review), Audit. Heavy lifting (classification,
 * group assessment, grading, artefact rendering) runs in the Clara
 * agentic mission and is reflected back here on poll.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAgentListFocus } from "@/hooks/useAgentListFocus";
import { useAppLocation } from "@/navigation/appLocation";
import { disclosureCodec } from "@/navigation/apps/disclosureLocation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUpload,
  faRefresh,
  faTriangleExclamation,
  faCircleCheck,
  faClipboardList,
  faFileLines,
  faFilePdf,
  faFileCsv,
  faFile,
  faSpinner,
  faTrash,
  faRobot,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import clsx from "clsx";

function disclosureErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}
import { missionControlTaskDetailPath } from "@/navigation/apps/missionControlLocation";
import { buildMissionDetailWindowUrl } from "@/navigation/apps/missionDetailLocation";
import { useOsNavigate } from "@/components/ui/os-layout/OsLink";
import { useIsOsMode } from "@/components/ui/os-layout/OsContext";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import * as AgenticV2API from "@/services/api/agentic-v2";
import {
  createCase,
  deleteCase,
  getCase,
  listCases,
  listDocuments,
  uploadDocument,
  type DisclosureCase,
  type DisclosureCaseStage,
  type DisclosureDocument,
  type DisclosureGrade,
  type RuleStatus,
  type ThresholdCrossing,
} from "@/services/api/disclosures";

import { PeerReviewTab } from "./PeerReviewTab";

// ── Helpers ───────────────────────────────────────────────────────────

const STAGE_LABEL: Record<DisclosureCaseStage, string> = {
  intake: "Intake",
  analyse: "Analyse",
  draft: "Draft",
  signoff: "Sign-off",
  threshold: "Threshold",
  triage: "Triage",
  completeness: "Completeness",
  assessment: "Assessment",
  review: "Review",
  filing: "Filing",
  submission: "Submission",
  complete: "Complete",
};

const GRADE_TONE: Record<string, string> = {
  "ready-to-file": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "minor-issues": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "needs-investigation": "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  "blocking-gap": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "do-not-file": "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};

const RULE_TONE: Record<RuleStatus, string> = {
  pending: "text-slate-500",
  passed: "text-emerald-600 dark:text-emerald-400",
  failed: "text-rose-600 dark:text-rose-400",
  unclear: "text-amber-600 dark:text-amber-400",
  "n/a": "text-slate-400",
};

function fileIcon(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return faFilePdf;
  if (ext === "csv") return faFileCsv;
  if (ext === "txt" || ext === "md") return faFileLines;
  return faFile;
}

function fmt(date?: string | null): string {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return date;
  }
}

// ── Tabs ──────────────────────────────────────────────────────────────

type CaseTab = "summary" | "documents" | "assessment" | "peer-review" | "audit";

const TABS: Array<{ id: CaseTab; label: string }> = [
  { id: "summary", label: "Summary" },
  { id: "documents", label: "Documents" },
  { id: "assessment", label: "Assessment" },
  { id: "peer-review", label: "Peer Review" },
  { id: "audit", label: "Audit" },
];

// ── Component ─────────────────────────────────────────────────────────

export function DisclosuresView() {
  const [location] = useAppLocation(disclosureCodec);
  const locationCaseId = location.caseId;
  const navigate = useNavigate();
  const osNavigate = useOsNavigate();
  const isOsMode = useIsOsMode();
  const windowId = useWindowId();
  const isOsUiActive = isOsMode || Boolean(windowId);

  const [cases, setCases] = useState<DisclosureCase[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const { focusedId: highlightedId } = useAgentListFocus({
    focusPrefix: "disclosure",
    rows: cases,
    getRowId: (row) => row.id,
    enabled: !loadingList && cases.length > 0,
  });
  const listOnlyFocus = !locationCaseId && Boolean(highlightedId);

  const [selectedId, setSelectedId] = useState<string | null>(
    listOnlyFocus ? null : (locationCaseId ?? null),
  );
  const [detailEnabled, setDetailEnabled] = useState(!listOnlyFocus && Boolean(locationCaseId));

  useEffect(() => {
    if (!locationCaseId) return;
    setSelectedId(locationCaseId);
    setDetailEnabled(true);
  }, [locationCaseId]);
  const [selected, setSelected] = useState<DisclosureCase | null>(null);
  const [documents, setDocuments] = useState<DisclosureDocument[]>([]);
  const [loadingCase, setLoadingCase] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CaseTab>("summary");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refreshCases = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await listCases();
      setCases(list);
      if (!selectedId && !listOnlyFocus && !highlightedId && list.length > 0) {
        setSelectedId(list[0].id);
      }
    } catch (e: unknown) {
      setError(disclosureErrorMessage(e, "Failed to load cases"));
    } finally {
      setLoadingList(false);
    }
  }, [highlightedId, listOnlyFocus, selectedId]);

  const refreshSelected = useCallback(async () => {
    if (!detailEnabled || !selectedId) {
      setSelected(null);
      setDocuments([]);
      return;
    }
    setLoadingCase(true);
    try {
      const [c, docs] = await Promise.all([
        getCase(selectedId),
        listDocuments(selectedId),
      ]);
      setSelected(c);
      setDocuments(docs);
    } catch (e: unknown) {
      setError(disclosureErrorMessage(e, "Failed to load case"));
    } finally {
      setLoadingCase(false);
    }
  }, [detailEnabled, selectedId]);

  useEffect(() => {
    refreshCases();
  }, [refreshCases]);

  useEffect(() => {
    refreshSelected();
  }, [refreshSelected]);

  // Reset to summary when changing case.
  useEffect(() => {
    setActiveTab("summary");
  }, [selectedId]);

  const onCreate = useCallback(async () => {
    setBusy("create");
    setError(null);
    try {
      const c = await createCase({
        title: `Disclosure draft ${new Date().toLocaleDateString()}`,
      });
      await refreshCases();
      setSelectedId(c.id);
    } catch (e: unknown) {
      setError(disclosureErrorMessage(e, "Failed to create case"));
    } finally {
      setBusy(null);
    }
  }, [refreshCases]);

  const onDelete = useCallback(
    async (id: string) => {
      if (!confirm("Delete this disclosure case? This cannot be undone.")) return;
      setBusy(`delete-${id}`);
      try {
        await deleteCase(id);
        if (selectedId === id) setSelectedId(null);
        await refreshCases();
      } catch (e: unknown) {
        setError(disclosureErrorMessage(e, "Failed to delete case"));
      } finally {
        setBusy(null);
      }
    },
    [refreshCases, selectedId],
  );

  const onUploadFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || !selectedId) return;
      setBusy("upload");
      setError(null);
      try {
        for (const file of Array.from(files)) {
          await uploadDocument(selectedId, file);
        }
        await refreshSelected();
      } catch (e: unknown) {
        setError(disclosureErrorMessage(e, "Upload failed"));
      } finally {
        setBusy(null);
      }
    },
    [refreshSelected, selectedId],
  );

  const openMissionControl = useCallback(
    (missionId: string, taskId: string | null) => {
      const path = taskId
        ? missionControlTaskDetailPath(taskId)
        : buildMissionDetailWindowUrl(missionId);
      if (isOsUiActive) {
        osNavigate(path, "Mission Control");
        return;
      }
      navigate(path);
    },
    [isOsUiActive, navigate, osNavigate],
  );

  const onRunClara = useCallback(async () => {
    setBusy("run-clara");
    setError(null);
    try {
      const result = await AgenticV2API.createDisclosureMission({});
      openMissionControl(result.mission_id, result.task_id);
    } catch (e: unknown) {
      setError(disclosureErrorMessage(e, "Failed to launch Clara mission"));
    } finally {
      setBusy(null);
    }
  }, [openMissionControl]);

  return (
    <div className="flex h-full min-h-0 bg-slate-50 dark:bg-transparent" data-testid="disclosures-view">
      {/* Left pane — case list */}
      <aside className="w-80 flex-shrink-0 flex flex-col border-r border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02]">
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-200 dark:border-white/5">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              Disclosures
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Clara — large position filings
            </p>
          </div>
          <button
            onClick={refreshCases}
            disabled={loadingList}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} className={loadingList ? "animate-spin" : ""} />
          </button>
        </div>

        <div className="p-3 border-b border-slate-200 dark:border-white/5 space-y-2">
          <button
            onClick={() => void onRunClara()}
            disabled={busy === "run-clara"}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-sky-500 via-blue-500 to-cyan-500 hover:opacity-90 text-white text-sm font-medium transition-opacity disabled:opacity-50 shadow-sm"
          >
            <FontAwesomeIcon
              icon={busy === "run-clara" ? faSpinner : faRobot}
              className={busy === "run-clara" ? "animate-spin" : ""}
            />
            Run Clara
          </button>
          <button
            onClick={onCreate}
            disabled={busy === "create"}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={busy === "create" ? faSpinner : faPlus} className={busy === "create" ? "animate-spin" : ""} />
            New disclosure case
          </button>
        </div>

        <ul className="flex-1 overflow-y-auto">
          {cases.length === 0 && !loadingList && (
            <li className="p-6 text-sm text-slate-500 dark:text-slate-400 text-center">
              No disclosure cases yet. Run Clara to start in Mission Control.
            </li>
          )}
          {cases.map((c) => (
            <CaseListItem
              key={c.id}
              c={c}
              selected={c.id === selectedId || c.id === highlightedId}
              onSelect={() => {
                setSelectedId(c.id);
                setDetailEnabled(true);
                if (!isOsUiActive) {
                  navigate(`/disclosures/${encodeURIComponent(c.id)}`);
                }
              }}
              onDelete={() => onDelete(c.id)}
            />
          ))}
        </ul>
      </aside>

      {/* Right pane — case workspace */}
      <main className="flex-1 flex flex-col min-h-0 overflow-y-auto">
        {error && (
          <div className="m-4 px-4 py-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-sm flex items-start gap-3">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700">
              ✕
            </button>
          </div>
        )}

        {!selected && !loadingCase && (
          <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500">
            <div className="text-center">
              <FontAwesomeIcon icon={faClipboardList} className="text-4xl mb-3" />
              <p>Select a case or create a new one.</p>
            </div>
          </div>
        )}

        {loadingCase && !selected && (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
          </div>
        )}

        {selected && (
          <CaseDetail
            disclosureCase={selected}
            documents={documents}
            busy={busy}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onUploadClick={() => fileInputRef.current?.click()}
            onRefresh={refreshSelected}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            onUploadFiles(e.target.files);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
      </main>
    </div>
  );
}

// ── Case list item ───────────────────────────────────────────────────

function CaseListItem({
  c,
  selected,
  onSelect,
  onDelete,
}: {
  c: DisclosureCase;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      data-focus-ref={`disclosure:${c.id}`}
      role="button"
      tabIndex={0}
      className={clsx(
        "group flex items-start gap-2 px-4 py-3 border-b border-slate-100 dark:border-white/5 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.04]",
        selected && "bg-sky-50 dark:bg-sky-500/10",
      )}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
          {c.title || c.issuer || c.id.slice(-8)}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {c.case_type !== "unknown" ? c.case_type : "Form not yet detected"} · {STAGE_LABEL[c.stage]}
        </div>
        {c.grade && <GradeBadge grade={c.grade} className="mt-1" />}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600"
        title="Delete"
      >
        <FontAwesomeIcon icon={faTrash} className="text-xs" />
      </button>
    </li>
  );
}

function GradeBadge({
  grade,
  className,
}: {
  grade: NonNullable<DisclosureGrade> | string;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full",
        GRADE_TONE[grade] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
        className,
      )}
    >
      {grade}
    </span>
  );
}

// ── Case detail (tabbed) ─────────────────────────────────────────────

interface CaseDetailProps {
  disclosureCase: DisclosureCase;
  documents: DisclosureDocument[];
  busy: string | null;
  activeTab: CaseTab;
  onTabChange: (tab: CaseTab) => void;
  onUploadClick: () => void;
  onRefresh: () => void;
}

function CaseDetail({
  disclosureCase: c,
  documents,
  busy,
  activeTab,
  onTabChange,
  onUploadClick,
  onRefresh,
}: CaseDetailProps) {
  const submitted = !!c.submitted_at;

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white truncate">
            {c.title || `Case ${c.id.slice(-8)}`}
          </h1>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            <span>
              {c.case_type !== "unknown" ? c.case_type : "Filing form not detected"}
            </span>
            <span>·</span>
            <span>{STAGE_LABEL[c.stage]}</span>
            {c.grade && <GradeBadge grade={c.grade} />}
            {submitted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                <FontAwesomeIcon icon={faCircleCheck} /> submitted
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
        </div>
      </header>

      {/* Tab bar */}
      <nav className="flex gap-1 border-b border-slate-200 dark:border-white/10 -mt-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className={clsx(
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              t.id === activeTab
                ? "border-sky-500 text-sky-600 dark:text-sky-400"
                : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200",
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      {activeTab === "summary" && <SummaryTab c={c} />}
      {activeTab === "documents" && (
        <DocumentsTab
          documents={documents}
          busy={busy}
          submitted={submitted}
          onUploadClick={onUploadClick}
        />
      )}
      {activeTab === "assessment" && <AssessmentTab c={c} />}
      {activeTab === "peer-review" && <PeerReviewTab caseId={c.id} caseTitle={c.title || `Case ${c.id.slice(-8)}`} />}
      {activeTab === "audit" && <AuditTab c={c} />}
    </div>
  );
}

// ── Tabs: Summary ────────────────────────────────────────────────────

function SummaryTab({ c }: { c: DisclosureCase }) {
  return (
    <>
      <Card>
        <Grid>
          <Field label="Holder" value={c.holder} />
          <Field label="Issuer" value={c.issuer} />
          <Field label="Filing deadline" value={c.filing_deadline ? fmt(c.filing_deadline) : null} />
          <Field
            label="Threshold crossings"
            value={c.threshold_crossings.length > 0 ? `${c.threshold_crossings.length} detected` : null}
          />
        </Grid>
      </Card>

      {c.threshold_crossings.length > 0 && (
        <Card title="Threshold crossings">
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {c.threshold_crossings.map((x: ThresholdCrossing, i: number) => (
              <li key={i} className="py-2 text-sm">
                <div className="text-slate-900 dark:text-white font-medium">
                  {x.holder} → {x.issuer}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {x.direction} through {x.threshold_tier} on {fmt(x.trade_date)} ·
                  now {x.holding_pct.toFixed(2)}%
                  {x.suggested_form && <> · {x.suggested_form}</>}
                  {x.earliest_filing_deadline && <> · deadline {fmt(x.earliest_filing_deadline)}</>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {c.completeness && (
        <Card title="Completeness">
          <div className="text-sm text-slate-700 dark:text-slate-300 mb-2">
            {c.completeness.summary || "—"}
          </div>
          {c.completeness.missing_documents.length > 0 && (
            <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
              {c.completeness.missing_documents.map((m) => (
                <li key={m.document_type}>
                  <span className={m.required ? "text-rose-600 font-medium" : ""}>
                    {m.required ? "required" : "recommended"}
                  </span>
                  : {m.document_name} — {m.reason}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {c.review && (
        <Card title="Filing readiness">
          <p className="text-sm text-slate-700 dark:text-slate-300">{c.review.summary}</p>
        </Card>
      )}

      {c.remedial_actions.length > 0 && (
        <Card title="Remedial actions">
          <ul className="text-xs space-y-1">
            {c.remedial_actions.map((a) => (
              <li key={a.id}>
                <span
                  className={clsx(
                    "px-1.5 py-0.5 rounded font-medium",
                    a.priority === "high"
                      ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                      : a.priority === "medium"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                  )}
                >
                  {a.priority}
                </span>{" "}
                <span className="font-medium text-slate-900 dark:text-white">{a.title}</span>
                <span className="text-slate-500 dark:text-slate-400"> — {a.description}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

// ── Tabs: Documents ──────────────────────────────────────────────────

function DocumentsTab({
  documents,
  busy,
  submitted,
  onUploadClick,
}: {
  documents: DisclosureDocument[];
  busy: string | null;
  submitted: boolean;
  onUploadClick: () => void;
}) {
  return (
    <Card
      title="Documents"
      action={
        <button
          onClick={onUploadClick}
          disabled={busy === "upload" || submitted}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium hover:opacity-90 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={busy === "upload" ? faSpinner : faUpload} className={busy === "upload" ? "animate-spin" : ""} />
          Upload
        </button>
      }
    >
      {documents.length === 0 ? (
        <Empty>No documents yet. Upload the position blotter and supporting files.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {documents.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-2 text-sm">
              <FontAwesomeIcon icon={fileIcon(d.filename)} className="text-slate-400 text-base" />
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 dark:text-white truncate">{d.filename}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {d.classification ? (
                    <>
                      <span className="font-medium">{d.classification}</span>
                      {typeof d.classification_confidence === "number" && (
                        <span> · {(d.classification_confidence * 100).toFixed(0)}%</span>
                      )}
                    </>
                  ) : (
                    <span className="italic">unclassified</span>
                  )}
                  {!d.valid && <span className="ml-2 text-rose-600">flagged invalid</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Tabs: Assessment ─────────────────────────────────────────────────

function AssessmentTab({ c }: { c: DisclosureCase }) {
  const groups = c.assessment?.groups || [];

  if (groups.length === 0) {
    return (
      <Card>
        <Empty>No assessment yet — Clara will run rule checks during the Analyse stage.</Empty>
      </Card>
    );
  }

  return (
    <Card title="Rule assessment">
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.group_id}>
            <div className="text-sm font-medium text-slate-900 dark:text-white">
              {g.group_name}
              <span className={clsx("ml-2 text-xs", RULE_TONE[g.status])}>{g.status}</span>
            </div>
            {g.reasoning && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{g.reasoning}</div>
            )}
            <ul className="text-xs space-y-0.5 ml-3">
              {g.rules.map((r) => (
                <li key={r.rule_id}>
                  <span className={clsx("font-mono", RULE_TONE[r.status])}>[{r.status}]</span>{" "}
                  <span className="text-slate-700 dark:text-slate-300">{r.label || r.rule_id}</span>
                  {r.evidence && (
                    <span className="text-slate-500 dark:text-slate-400"> — {r.evidence}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Tabs: Audit ──────────────────────────────────────────────────────

function AuditTab({ c }: { c: DisclosureCase }) {
  const events = (c.metadata?.audit_events as Array<{
    at: string;
    event_type: string;
    summary: string;
    actor?: string;
  }> | undefined) || [];

  return (
    <Card title="Audit trail">
      {events.length === 0 ? (
        <Empty>No audit events yet.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {events.slice().reverse().map((e, i) => (
            <li key={i} className="py-2 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-slate-800 dark:text-slate-200">{e.summary}</span>
                <span className="text-xs text-slate-400 whitespace-nowrap">{fmt(e.at)}</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {e.event_type}{e.actor && <> · {e.actor}</>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-400 mt-3">
        Full plain-English audit pack is rendered as a versioned artefact during Sign-off.
      </p>
    </Card>
  );
}

// ── Tiny presentational helpers ──────────────────────────────────────

function Card({
  title,
  action,
  children,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white dark:bg-white/[0.03] rounded-xl border border-slate-200 dark:border-white/10 p-4">
      {(title || action) && (
        <div className="flex items-center justify-between mb-3">
          {title && (
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>;
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-900 dark:text-white">{value || "—"}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">{children}</div>
  );
}

export { GRADE_TONE };
export default DisclosuresView;
